import { call, fork, put, select, take, takeEvery, throttle } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { Map } from 'immutable';
// import * as Sentry from '@sentry/browser'
import request from 'helpers/api';
import { interval, onEvent } from 'helpers/saga';
import history from 'helpers/history';
import { randomString, specialText } from 'helpers/string';

import { getActiveBookId, getActiveOrderId } from 'redux/selectors/app';
import { getProfile, isAuthenticated } from 'redux/selectors/user';
import { getDesignSpreads, getSpreadDesign } from 'redux/selectors/design';
import {
  canEdit,
  getAddress,
  getChildBirthday,
  getChildId,
  getChildName,
  getOrder,
  isWaitingForPayment,
} from 'redux/selectors/orders';
import { getStats } from 'redux/selectors/stats';

import { SET_ACTIVE_ORDER, SET_ACTIVE_SPREAD, setOrderValidation, setSidebar, setUIState } from 'redux/reducers/app';
import {
  add,
  DISABLE_EXTRA_SPREAD,
  ENABLE_EXTRA_SPREAD,
  FINISH,
  GET_ORDERS,
  PICK_MOMENT,
  renameId,
  SAVE,
  SET_OPTION,
  setAll,
  START_PAYMENT,
  START_PAYMENT_RENEW,
  UPDATE_TEXT,
  UPDATE_TRANSFORM,
  updateText,
} from 'redux/reducers/orders';
import { SET_UPLOAD, TOGGLE_FAVORITE, TOGGLE_SAVED } from 'redux/reducers/photos';
import { logout, trackEvent, UPDATE_PROFILE } from 'redux/reducers/user';
import { formatBirthday } from '../../helpers/time';

const SAVE_THROTTLE = 5000;
const VALIDATION_THROTTLE = 2000;

let hasChangesToSave = false;

export function* getSingleOrder({ id, force = false }) {
  try {
    const authenticated = yield select(state => isAuthenticated(state));
    if (id && authenticated) {
      const existingOrder = yield select(state => state.getIn(['orders', id]));

      if (force || !existingOrder) {
        yield put(setUIState('loading_orders', true));
        const order = yield call(request, 'orders/get/' + id, {
          auth: true,
        });

        if (order) {
          yield put(add(id, order));
        }

        yield put(setUIState('loading_orders', false));

        return yield select(state => state.getIn(['orders', id]));
      } else {
        return existingOrder;
      }
    }
  } catch (e) {
    pe(e);

    yield put(
      trackEvent('error', {
        type: 'getSingleOrder',
        id,
        e: e.toString(),
      })
    );
  }
}

export function* getOrders() {
  try {
    const authenticated = yield select(state => isAuthenticated(state));
    if (authenticated) {
      yield put(setUIState('loading_orders', true));

      if (hasChangesToSave) {
        yield call(saveOrder);
      }

      const orders = yield call(request, 'orders/all', {
        auth: true,
      });

      const ids = Object.keys(orders);
      if (ids.length > 0) {
        yield put(setAll(orders));

        // Set default order when none is set yet
        // const activeOrderId = yield select(state => getActiveOrderId(state))
        // if (!activeOrderId) {
        // const firstOrder = orders[ids[0]]
        // history.replace('/' + firstOrder.book + '/' + firstOrder.id)
        // }
      }

      yield put(setUIState('loading_orders', false));

      return yield select(state => state.get('orders'));
    }
  } catch (e) {
    pe(e);

    yield put(
      trackEvent('error', {
        type: 'getOrders',
        e: e.toString(),
      })
    );
  }
}

/**
 * Create order if it doesn't exist, or when multiple orders are available, ask the user
 */
export function* createNewOrder({
  book = null,
  name = '',
  birthday = '',
  onlyWhenEmpty = true,
  redirect = false,
  pushHistory = true,
}) {
  try {
    const existingOrders = yield select(state => state.get('orders', Map()));

    // If no orders exist, create new local one
    if (existingOrders.count() === 0 || !onlyWhenEmpty) {
      const id = randomString();

      let activeBookId = book;
      if (!book) {
        activeBookId = yield select(state => getActiveBookId(state));
      }
      const currentBook = yield select(state => state.get('books').find(book => book.get('id') === activeBookId));
      const designId = currentBook.get('default_design');
      const first_name = yield select(state => getProfile(state).get('first_name'));

      const order = {
        local: true,
        design: designId,
        book: activeBookId,
        user: 0,
        amount: 1,
        terms: false,
        state: 'creating',
        child: {
          name,
          birthday,
        },
        uploads: {},
        favorites: {},
        address: {
          first_name,
          country: global.kroostApp.country,
        },
        picked: {},
        saved: [],
        extra_spreads: [],
      };

      yield put(add(id, order));

      if (id) {
        if (pushHistory) {
          history[redirect ? 'push' : 'replace']('/' + activeBookId + '/' + id);
        }
        return id;
      }
    }
  } catch (e) {
    pe(e);

    yield put(
      trackEvent('error', {
        type: 'createNewOrder',
        e: e.toString(),
      })
    );
  }
}

export function* saveOrder() {
  try {
    const authenticated = yield select(state => isAuthenticated(state));
    const editable = yield select(state => canEdit(state));

    if (authenticated && editable) {
      const orderData = yield select(state => {
        const order = getOrder(state).toJS();

        // try {
        //   if (!order.uploads) {
        //     const debugData = JSON.stringify({
        //       activeOrderId: state.getIn(['app', 'activeOrderId'])
        //     })
        //     Sentry.captureMessage('Order somehow reset: ' + debugData)
        //   }
        // } catch (e) {
        //   pe(e)
        // }

        return {
          uid: getActiveOrderId(state),
          ...order,
          //child: null,
          local: order.local || false,
          uploads: JSON.stringify(order.uploads),
          favorites: JSON.stringify(order.favorites),
          picked: JSON.stringify(order.picked),
          saved: JSON.stringify(order.saved),
          extra_spreads: JSON.stringify(order.extra_spreads || []),
          stats: JSON.stringify(getStats(state).toJS()),
          address: JSON.stringify(order.address),
          child_id: getChildId(state),
          child_name: getChildName(state),
          child_birthday: getChildBirthday(state),
        };
      });
      const { local, uid } = orderData;

      const response = yield call(request, 'orders/save', {
        method: 'post',
        auth: true,
        body: orderData,
      });

      if (response) {
        const { uid: orderUID, tracking_data } = response;

        if (local) {
          yield put(renameId(uid, orderUID));
          history.replace(history.location.pathname.replace(uid, orderUID));
        }

        if (tracking_data) {
          yield put(
            trackEvent('order-create', {
              orderUID,
              ...tracking_data,
            })
          );
        }

        yield orderUID;
      } else {
        console.warn('Something went wrong with saving order');
        // Sentry.captureMessage('Something went wrong with saving order')

        yield put(
          trackEvent('error', {
            type: 'saveOrder 1',
            orderData: JSON.stringify(orderData),
          })
        );
      }
    }
  } catch (e) {
    pe(e);

    // Force logout when we get 4xx error while saving
    if (e >= 400 && e < 500) {
      yield put(logout());
    }

    yield put(
      trackEvent('error', {
        type: 'saveOrder 2',
        e: e.toString(),
      })
    );
  }
}

function* saveOnChange() {
  while (true) {
    const initializing = yield select(state => state.getIn(['app', 'ui', 'initializing'], true));

    if (!initializing && hasChangesToSave) {
      hasChangesToSave = false;
      yield call(saveOrder);
    }

    yield delay(SAVE_THROTTLE);
  }
}

function* setHasChanged() {
  hasChangesToSave = true;
}

function* trackOrderActions({ type, ...rest }) {
  try {
    let event;
    let data;

    switch (type) {
      case TOGGLE_SAVED:
      case TOGGLE_FAVORITE: {
        const { orderId, currentSpread, simpleId } = rest;

        const typeSaved = type === TOGGLE_SAVED;
        const saved = yield select(state => {
          return state.getIn(
            typeSaved ? ['orders', orderId, 'saved'] : ['orders', orderId, 'favorites', currentSpread]
          );
        });

        const index = saved.findIndex(savedSimpleId => savedSimpleId === simpleId);

        event =
          index === -1
            ? typeSaved
              ? 'photo_unpinned'
              : 'photo_deselected'
            : typeSaved
            ? 'photo_pinned'
            : 'photo_selected';

        data = {};
        break;
      }
      case ENABLE_EXTRA_SPREAD: {
        event = 'spread_added';
        data = {};

        break;
      }
      case DISABLE_EXTRA_SPREAD: {
        event = 'spread_removed';
        data = {};
        break;
      }
      case SET_OPTION: {
        if (rest.option === 'child') {
          // Same selected do nothing
          const value = rest.value.toJS();
          if (rest.options.previousId !== value.id) {
            event = 'child_changed';
            data = {
              child_id: value.id,
              child_name: value.name,
              child_dob: value.birthday,
            };
          }
        }

        break;
      }
    }

    if (event) {
      yield put(trackEvent(event, data));
    }
  } catch (e) {
    pe(e);

    yield put(
      trackEvent('error', {
        type: 'trackOrderActions',
        e: e.toString(),
      })
    );
  }
}

export function* startPayment({ orderId }) {
  yield put(setUIState('failed_validating', false));
  yield put(setUIState('validating', true));

  try {
    // Save order
    yield call(saveOrder);

    // Validate address
    yield validateOrderForm({ option: ['address'] });

    // Do we have any validating to do?
    const validation = yield select(state => state.getIn(['app', 'orderValidation', getActiveOrderId(state)]));
    const allTrue = validation.every(value => value === true);

    if (allTrue) {
      yield put(setUIState('payment_forwarding', true));

      // Get payment info
      const paymentInfo = yield call(request, 'orders/pay/' + orderId, {
        auth: true,
      });

      if (paymentInfo) {
        window.location = paymentInfo.paymentUrl;
      }
    } else {
      yield put(setUIState('failed_validating', true));
    }
  } catch (e) {
    pe(e);

    yield put(
      trackEvent('error', {
        type: 'startPayment',
        e: e.toString(),
      })
    );
  }

  yield put(setUIState('validating', false));
}

export function* finishOrder({ orderId }) {
  yield put(setUIState('failed_validating', false));
  yield put(setUIState('validating', true));

  try {
    // Save order
    yield call(saveOrder);

    // Validate address
    yield validateOrderForm({ option: ['address'] });

    // Do we have any validating to do?
    const validation = yield select(state => state.getIn(['app', 'orderValidation', getActiveOrderId(state)]));
    const allTrue = validation.every(value => value === true);

    if (allTrue) {
      // Get payment info
      const finishInfo = yield call(request, 'orders/finish/' + orderId, {
        auth: true,
      });

      if (finishInfo) {
        yield put(add(orderId, finishInfo));
      }
    } else {
      yield put(setUIState('failed_validating', true));
    }
  } catch (e) {
    pe(e);

    yield put(
      trackEvent('error', {
        type: 'finishError',
        e: e.toString(),
      })
    );
  }

  yield put(setUIState('validating', false));
}

export function* startPaymentRenew({ orderId }) {
  yield put(setUIState('resetting_state', true));

  try {
    // Reset book to "creating"
    const reset = yield call(request, 'orders/reset/' + orderId, {
      auth: true,
    });

    if (reset) {
      yield put(setSidebar('order'));
      yield call(getSingleOrder, { id: orderId, force: true });
      // yield call(startPayment, { orderId });
    }
  } catch (e) {
    pe(e);

    yield put(
      trackEvent('error', {
        type: 'startPaymentRenew',
        e: e.toString(),
      })
    );
  }

  yield put(setUIState('resetting_state', false));
}

/**
 * Force save after coming online
 */
export function* saveOnOnline() {
  const onOnlineChannel = yield call(onEvent, window, 'online');

  while (true) {
    yield take(onOnlineChannel);
    yield call(setHasChanged);
  }
}

/**
 * Validate order form
 */
export function* validateOrderForm({ option }) {
  try {
    if (Array.isArray(option) && option[0] === 'address') {
      const address = yield select(state => getAddress(state));

      const validated = yield call(request, 'orders/validate/address', {
        method: 'post',
        body: {
          address: JSON.stringify(address.toJS()),
        },
      });

      if (validated) {
        yield put(setOrderValidation('address', validated));
      }
    }
  } catch (e) {
    pe(e, 'failed validateOrderForm');
  }
}

/**
 * When changing special text (or setting active order)
 */
export function* setSpecialTexts({ option, type }) {
  try {
    let child, name;
    try {
      [child, name] = option;
    } catch (e) {}

    if ((child === 'child' && (name === 'name' || name === 'birthday')) || type === SET_ACTIVE_ORDER) {
      try {
        const orderId = yield select(state => getActiveOrderId(state));
        const name = yield select(state => getChildName(state));
        const birthday = yield select(state => getChildBirthday(state));
        const spreadDesigns = yield select(state => getDesignSpreads(state));

        const set = [];

        spreadDesigns.forEach(spreadDesign => {
          spreadDesign
            ? spreadDesign.get('moments', []).forEach(moment => {
                if (moment.getIn(['options', 'special_text'], false)) {
                  set.push({
                    momentId: moment.get('id'),
                    text: specialText('', name, birthday, moment.getIn(['options'])),
                  });
                }
              })
            : null;
        });

        for (let item in set) {
          const { momentId, text } = set[item];
          yield put(updateText(orderId, momentId, text));
        }
      } catch (e) {
        pe(e, 'failed setSpecialTexts');
      }
    }
  } catch (e) {
    pe(e);
  }
}

/**
 * When first time on a spread, check and see if default text need to be saved/set
 */
export function* setDefaultText({ options }) {
  try {
    const order = yield select(state => getOrder(state));
    const orderId = yield select(state => getActiveOrderId(state));
    const spreadDesign = yield select(state => getSpreadDesign(state));
    const name = yield select(state => getChildName(state));
    const birthday = yield select(state => getChildBirthday(state));
    const date_format = 'D MMMM YYYY';

    const set = [];

    spreadDesign &&
      spreadDesign.get('moments', []).forEach(moment => {
        const text = moment
          .getIn(['options', 'placeholder_text'], '')
          .replace(/{name}/gi, name)
          .replace(/{birthday}/gi, formatBirthday(birthday, date_format));
        const momentId = moment.get('id');
        let overwrite = false;

        // Compare current to previous, if they match, we can overwrite the placeholder_text again
        if (options && 'previousName' in options && 'previousBirthDay' in options) {
          const previousText = moment
            .getIn(['options', 'placeholder_text'], '')
            .replace(/{name}/gi, options.previousName || '')
            .replace(
              /{birthday}/gi,
              options.previousBirthDay ? formatBirthday(options.previousBirthDay, date_format) : ''
            );

          const currentPicked = order.getIn(['picked', momentId, 'text']);
          overwrite = previousText === currentPicked;
        }

        if (
          (!order.getIn(['picked', momentId]) &&
            text &&
            moment.getIn(['options', 'placeholder_text_remove_on_edit']) === false) ||
          overwrite
        ) {
          set.push({ momentId, text });
        }
      });

    for (let item in set) {
      const { momentId, text } = set[item];
      yield put(updateText(orderId, momentId, text));
    }
  } catch (e) {
    pe(e, 'failed setDefaultText');
  }
}

/**
 * Poll current order if state is payment_waiting
 */
export function* pollPaymentStatus() {
  const onPaymentPoll = yield call(interval, 5);

  while (true) {
    yield take(onPaymentPoll);

    const isWaiting = yield select(state => isWaitingForPayment(state));

    if (isWaiting) {
      const orderUID = yield select(state => getActiveOrderId(state));
      const orderState = yield call(request, 'orders/check-payment/' + orderUID, {
        auth: true,
      });

      // Update order if status has changed
      if (orderState !== 'payment_waiting') {
        yield getSingleOrder({ id: orderUID, force: true });
      }
    }
  }
}

export default function* saga() {
  // yield takeEvery(CREATE_ORDER, createNewOrder)
  yield takeEvery(SET_ACTIVE_ORDER, getSingleOrder);
  yield takeEvery(GET_ORDERS, getOrders);
  yield takeEvery(FINISH, finishOrder);
  yield takeEvery(START_PAYMENT, startPayment);
  yield takeEvery(START_PAYMENT_RENEW, startPaymentRenew);
  yield takeEvery(SET_ACTIVE_SPREAD, setDefaultText);
  yield takeEvery(UPDATE_TEXT, setDefaultText);
  yield takeEvery(SET_OPTION, setDefaultText);
  yield throttle(VALIDATION_THROTTLE, SET_OPTION, setSpecialTexts);

  yield throttle(VALIDATION_THROTTLE, SET_OPTION, validateOrderForm);

  yield fork(saveOnOnline);
  yield fork(pollPaymentStatus);

  const saveActions = [
    TOGGLE_SAVED,
    TOGGLE_FAVORITE,
    PICK_MOMENT,
    SET_UPLOAD,
    PICK_MOMENT,
    SAVE,
    UPDATE_PROFILE,
    UPDATE_TRANSFORM,
    UPDATE_TEXT,
    SET_OPTION,
    ENABLE_EXTRA_SPREAD,
    DISABLE_EXTRA_SPREAD,
  ];
  yield takeEvery(saveActions, setHasChanged);

  yield takeEvery(
    [TOGGLE_FAVORITE, TOGGLE_SAVED, ENABLE_EXTRA_SPREAD, DISABLE_EXTRA_SPREAD, FINISH, SET_OPTION],
    trackOrderActions
  );

  yield fork(saveOnChange);
}



// WEBPACK FOOTER //
// ./app/redux/sagas/orders.js